import {
  Box,
  Button,
  Checkbox,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  ModalIsland,
  Typography,
} from "@saviynt/design-system";
import { type Dispatch, type SetStateAction, useState } from "react";
import { useIntl } from "react-intl";
import { useCreateSaveFilterView } from "../../../../hooks/usePolicySaveFilterView";
import { parseError } from "../../../PoliciesFindingsList/utils";
import { sanitizeUserInput } from "@repo/mfe-core";
import {
  errorMessages,
  VIEW_NAME_REGEX,
} from "../../../../constants/filterSaveView.constants";
import { usePoliciesFilter } from "../../../../context/PoliciesFilterContext";

interface SaveViewModalProps {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
}

export default function SaveViewModal({
  isModalOpen,
  setIsModalOpen,
}: SaveViewModalProps) {
  const { filterValues } = usePoliciesFilter();
  const intl = useIntl();
  const { mutate } = useCreateSaveFilterView();

  const [viewName, setViewName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const closeModal = () => {
    setViewName("");
    setIsDefault(false);
    setErrorMessage("");
    setIsModalOpen(false);
  };

  const handleError = (err: unknown) => {
    const { message, code = "" } = parseError(err);
    setErrorMessage(
      code in errorMessages
        ? intl.formatMessage(errorMessages[code as keyof typeof errorMessages])
        : message,
    );
  };

  const handleSubmit = () => {
    if (!viewName) {
      setErrorMessage(intl.formatMessage(errorMessages.MIN_CHAR));
      return;
    }
    if (!VIEW_NAME_REGEX.test(viewName)) {
      setErrorMessage(
        intl.formatMessage(errorMessages.NO_SPECIAL_CHAR_ALLOWED),
      );
      return;
    }

    mutate(
      {
        is_default: isDefault,
        view_name: sanitizeUserInput(viewName),
        preference: {
          searchValue: filterValues.searchValue,
          selectedFilters: filterValues.selectedFilters as unknown as Record<
            string,
            Record<string, unknown>[]
          >,
        },
      },
      { onSuccess: closeModal, onError: handleError },
    );
  };

  return (
    <ModalIsland
      onClose={closeModal}
      isOpen={isModalOpen}
      title={intl.formatMessage({
        description:
          "Modal title for saving current policies filter and sort preferences as a custom view",
        defaultMessage: "Save View",
        id: "Bss3GD",
      })}
      subtitle={intl.formatMessage({
        id: "j+oF+Q",
        defaultMessage:
          "The configurations and filters you've applied can be saved for future certifications. Give it a name that's easy to reference.",
        description:
          "Subtitle explaining the purpose of saving views in the policies save view modal",
      })}
      contentBody={
        <Box className="grid gap-4">
          <InputField
            name="viewName"
            isRequired
            placeholder={intl.formatMessage({
              description:
                "Placeholder text for the view name input field in the NHI save view modal",
              defaultMessage: "Provide a name for this view",
              id: "ptf9fi",
            })}
            label={
              <Typography kind="label">
                {intl.formatMessage({
                  description:
                    "Label for the view name input field in the NHI save view modal",
                  defaultMessage: "View Name",
                  id: "As26Ky",
                })}
              </Typography>
            }
            type="text"
            value={viewName}
            setValue={setViewName}
            isCritical={!!errorMessage}
            setIsCritical={() => setErrorMessage("")}
            CriticalHelperText={
              <InlineMessage
                text={errorMessage}
                colorTheme="critical"
                size="small"
                leftIcon={<Icon kind="AlertCriticalFilled" />}
              />
            }
          />
          <Checkbox
            kind="singleLine"
            label={intl.formatMessage({
              description:
                "Checkbox label to set the saved NHI inventory view as the default view for the user",
              defaultMessage: "Make this my default view.",
              id: "nFCeKd",
            })}
            showHoverEffect
            isChecked={isDefault}
            onChange={(event) => setIsDefault(event.target.checked)}
            classNames={{ inputContainerClassName: "ml-auto" }}
          />
        </Box>
      }
      FooterActionBarComp={
        <FooterActionBar size="large">
          <div className="flex pl-2 gap-4 ml-auto">
            <Button kind="outlined" size="medium" onClick={closeModal}>
              {intl.formatMessage({
                id: "RbfuXN",
                defaultMessage: "Cancel",
                description:
                  "Button text to cancel saving the policies view and close the modal",
              })}
            </Button>
            <Button kind="filled" size="medium" onClick={handleSubmit}>
              {intl.formatMessage({
                defaultMessage: "Save",
                id: "wSRuxR",
                description:
                  "Button text to save the policies view with the specified name and preferences",
              })}
            </Button>
          </div>
        </FooterActionBar>
      }
    />
  );
}
